import { DeleteOutlined, DoubleLeftOutlined, DoubleRightOutlined, DownOutlined, PictureOutlined, PlusOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button, Form, Image, Input, message, Select, Space, Switch, Tooltip, Typography, Upload } from 'antd';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { flushSync } from 'react-dom';
import axios from '../api';
import theme from '../theme';
import {
  getDifyChatStorageKey,
  getUserId,
  STORAGE_KEYS
} from '../utils/storage';
import ChatHistoryList from './ChatHistoryList';
import ChatConversationSidebar from './ChatConversationSidebar';
import ChatMessageList from './ChatMessageList';
import { getModelApiConfig } from '../features/setting/ApiManage';
import { createDifyChatAPI, DifyDataTransformer } from '../utils/difyChat';
import DynamicForm from './DynamicForm';
import DifyAppService from '../utils/difyAppService';
import AgentAvatar from '../components/AgentAvatar';
import {
  createStreamAnswerExtractor,
  createStreamFlusher,
  estimateStreamRevealMs,
  generateTempMessageId,
  mergeStreamChunk,
  upsertAssistantStreamMessage,
} from './streamUtils';
import './chat-messages.css';
import './chat-workspace.css';

/** 平台 Agent 使用数字 id；非数字 id（如 school_qa_agent）走旧直连 */
const isPlatformAgentId = (id) => id != null && /^\d+$/.test(String(id));

// 性能监控 hook - 优化版本，只在开发环境且渲染间隔小于16ms时才警告
const usePerformanceMonitor = (componentName) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const warningShown = useRef(false);

  useEffect(() => {
    renderCount.current += 1;
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderTime.current;
    lastRenderTime.current = now;

    // 只在开发环境且渲染间隔小于16ms且未显示过警告时才警告
    if (process.env.NODE_ENV === 'development' && timeSinceLastRender < 16 && !warningShown.current) {
      console.warn(`${componentName} 渲染过于频繁 (${timeSinceLastRender}ms)，可能存在性能问题`);
      warningShown.current = true; // 只显示一次警告，避免刷屏
    }
  });

  return {
    renderCount: renderCount.current,
    timeSinceLastRender: Date.now() - lastRenderTime.current
  };
};

// 生成唯一ID，临时ID以temp_开头，包含时间戳和随机数
const uuid = () => '_' + Math.random().toString(36).slice(2, 10) + Date.now();

// 防抖函数 - 优化版本
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};


// 生成对话标题
const generateTitle = (messages, t) => {
  // 找到第一条用户消息作为标题
  const firstUserMessage = messages.find(msg => msg.role === 'user');
  if (firstUserMessage && firstUserMessage.content) {
    const content = firstUserMessage.content.trim();
    // 如果内容太长，截取前20个字符
    return content.length > 20 ? content.substring(0, 20) + '...' : content;
  }
  return t('common.newConversation');
};

const getDefaultConversation = (agent = null, welcomeText, t) => ({
  id: uuid(),
  title: '',
  messages: [{ role: 'system', content: agent?.welcomeText || welcomeText || t('common.welcomeToDifyAgent') }],
  updatedAt: Date.now(),
});

const LOCAL_KEY = STORAGE_KEYS.DIFY_CONVERSATIONS;
const LOCAL_CUR = STORAGE_KEYS.DIFY_CURRENT_ID;
const LOCAL_SIDEBAR = STORAGE_KEYS.DIFY_SIDEBAR;

// =============================================
// ChatPanelDifyChatflowApi
// 全功能智能体聊天面板（Dify Chatflow API专用）
// 说明：所有智能体聊天相关UI和逻辑全部内聚于本组件，外部只需传递agent、apiKey、apiUrl、welcomeText等参数
// 外部无需做任何条件分支或特殊处理
// =============================================

const ChatPanelDifyChatflowApi = ({
  agentList = [],
  currentAgent = null,
  onAgentChange,
  agentId: propAgentId,
  welcomeText = '欢迎使用 Dify 智能体',
  namespace = 'dify',
  apiKey,
  apiUrl,
  showModelSelection = false,  // 是否显示大模型选择
  showKnowledgeBase = false,   // 是否显示知识库选择
  showImageUpload = true,      // 是否显示图片上传功能
  defaultModel = '',    // 默认模型（空值，由组件自动选择第一个可用模型）
  defaultKnowledgeBase = 'none', // 默认知识库
  responseMode = 'blocking', // 响应模式：'blocking' 非流式, 'streaming' 流式
  showDynamicForm = true,       // 是否启用动态表单
  agentSidebar = null,          // 统一侧边栏模式：智能体区域（与对话历史上下排列）
  ...restProps
}) => {
  // 性能监控
  const performance = usePerformanceMonitor('ChatPanelDifyChatflowApi');
  const { t } = useTranslation();
const { Option } = Select;
const { Text } = Typography;
  const agentId = propAgentId || currentAgent?.id;
  const userInfo = restProps.userInfo;

  // 简化状态管理：只保留必要的状态
  const [input, setInput] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sending, setSending] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const chatListRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatHistoryListRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // 动态表单相关状态
  const [appConfig, setAppConfig] = useState(null);
  const [formSchema, setFormSchema] = useState([]);
  const [appLoading, setAppLoading] = useState(false);
  const [dynamicInputs, setDynamicInputs] = useState({});
  const [dynamicForm] = Form.useForm();
  const [includeIdentity, setIncludeIdentity] = useState(false);
  const initializedRef = useRef(false);

  // 新增状态：用户滚动状态、回到底部按钮、AI输出状态
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [showBackToBottom, setShowBackToBottom] = useState(false);
  const [aiMessageContent, setAiMessageContent] = useState('');
  const [isAiOutputting, setIsAiOutputting] = useState(false);
  
  // 统一的消息列表，不区分实时消息和历史消息
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [switchingConversation, setSwitchingConversation] = useState(false);
  const [selectedDifyConversationId, setSelectedDifyConversationId] = useState(null);

  // 使用 ref 存储滚动相关状态，避免闭包陈旧 & 程序化滚动误判为用户手动滚动
  const isAiOutputtingRef = useRef(false);
  const userHasScrolledRef = useRef(false);
  const autoScrollRef = useRef(true);
  const isAutoScrollingRef = useRef(false);
  const autoScrollClearTimerRef = useRef(null);
  const handleScrollTimeoutRef = useRef(null);
  const prevLoadingMessagesRef = useRef(false);
  const SCROLL_BOTTOM_THRESHOLD = 80;

  const identityStorageKey = useMemo(
    () => (agentId ? `chat_include_identity_${agentId}` : null),
    [agentId]
  );
  const hasStudentBindingMap = useCallback((bindings) => {
    if (!bindings || typeof bindings !== 'object') return false;
    return Object.keys(bindings).some((key) => /student/i.test(String(key)));
  }, []);
  const hasIdentityBindings = Boolean(
    appConfig?.has_identity_bindings
    || hasStudentBindingMap(appConfig?.input_bindings)
    || hasStudentBindingMap(currentAgent?.input_bindings)
  );
  const canPassIdentity = hasIdentityBindings && Boolean((userInfo?.student_no || '').trim());

  useEffect(() => {
    userHasScrolledRef.current = userHasScrolled;
  }, [userHasScrolled]);

  useEffect(() => {
    autoScrollRef.current = autoScroll;
  }, [autoScroll]);

  const getScrollContainer = useCallback(() => chatContainerRef.current, []);

  const isNearBottom = useCallback((container) => {
    const el = container || getScrollContainer();
    if (!el) return true;
    const { scrollTop, scrollHeight, clientHeight } = el;
    return scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD;
  }, [getScrollContainer]);

  const scrollToBottom = useCallback(({ force = false, behavior = 'auto' } = {}) => {
    if (!force && (userHasScrolledRef.current || !autoScrollRef.current)) return;

    const container = getScrollContainer();
    const endEl = messagesEndRef.current;
    if (!container && !endEl) return;

    isAutoScrollingRef.current = true;
    if (autoScrollClearTimerRef.current) {
      clearTimeout(autoScrollClearTimerRef.current);
    }

    if (endEl) {
      endEl.scrollIntoView({ block: 'end', behavior });
    } else if (container) {
      const target = Math.max(0, container.scrollHeight - container.clientHeight);
      if (behavior === 'smooth') {
        container.scrollTo({ top: target, behavior: 'smooth' });
      } else {
        container.scrollTop = target;
      }
    }

    autoScrollClearTimerRef.current = setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, 120);
  }, [getScrollContainer]);

  const scrollToBottomInstant = useCallback(() => {
    scrollToBottom({ force: true });
  }, [scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (isAutoScrollingRef.current) return;

    const container = getScrollContainer();
    if (!container) return;

    if (handleScrollTimeoutRef.current) {
      clearTimeout(handleScrollTimeoutRef.current);
    }

    handleScrollTimeoutRef.current = setTimeout(() => {
      if (isAutoScrollingRef.current) return;

      if (isNearBottom(container)) {
        userHasScrolledRef.current = false;
        autoScrollRef.current = true;
        setUserHasScrolled(false);
        setShowBackToBottom(false);
        setAutoScroll(true);
      } else {
        userHasScrolledRef.current = true;
        autoScrollRef.current = false;
        setUserHasScrolled(true);
        setAutoScroll(false);
        setShowBackToBottom(true);
      }
    }, 50);
  }, [getScrollContainer, isNearBottom]);

  useEffect(() => {
    const container = getScrollContainer();
    if (!container) return undefined;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (handleScrollTimeoutRef.current) {
        clearTimeout(handleScrollTimeoutRef.current);
      }
    };
  }, [getScrollContainer, handleScroll, selectedDifyConversationId, messages.length]);

  // ChatGPT 风格：消息更新时保持贴底（流式输出 / 新消息）
  useEffect(() => {
    if (switchingConversation || loadingMessages) return;
    if (messages.length === 0) return;
    if (userHasScrolledRef.current || !autoScrollRef.current) return;

    requestAnimationFrame(() => {
      scrollToBottom({ force: true });
    });
  }, [
    messages,
    isAiOutputting,
    sending,
    switchingConversation,
    loadingMessages,
    scrollToBottom,
  ]);

  // 流式输出过程中，每次内容增量更新都贴底
  useEffect(() => {
    if (!isAiOutputting && !sending) return;
    if (userHasScrolledRef.current) return;

    requestAnimationFrame(() => {
      scrollToBottom({ force: true });
    });
  }, [aiMessageContent, isAiOutputting, sending, scrollToBottom]);

  // 历史会话加载完成后滚到底部
  useEffect(() => {
    const justFinished = prevLoadingMessagesRef.current && !loadingMessages;
    prevLoadingMessagesRef.current = loadingMessages;

    if (!justFinished || messages.length === 0) return undefined;

    setUserHasScrolled(false);
    setAutoScroll(true);
    setShowBackToBottom(false);

    const scroll = () => scrollToBottom({ force: true });
    scroll();
    const timers = [50, 150, 400, 800].map((delay) => setTimeout(scroll, delay));
    return () => timers.forEach(clearTimeout);
  }, [loadingMessages, messages.length, scrollToBottom]);

  // StreamText / Markdown 高度变化时继续贴底
  useEffect(() => {
    const thread = chatListRef.current;
    if (!thread) return undefined;

    const observer = new ResizeObserver(() => {
      if (userHasScrolledRef.current || !autoScrollRef.current) return;
      if (switchingConversation || loadingMessages) return;
      scrollToBottom({ force: true });
    });

    observer.observe(thread);
    return () => observer.disconnect();
  }, [
    messages.length,
    switchingConversation,
    loadingMessages,
    scrollToBottom,
  ]);

  useEffect(() => {
    isAiOutputtingRef.current = isAiOutputting;
  }, [isAiOutputting]);

  // 用户发送消息时恢复自动贴底
  useEffect(() => {
    if (sending) {
      userHasScrolledRef.current = false;
      autoScrollRef.current = true;
      setUserHasScrolled(false);
      setAutoScroll(true);
      setShowBackToBottom(false);
    }
  }, [sending]);

  const useDifyHistory = true; // 这个组件专门用于Dify，所以总是使用Dify历史记录
  
  // 新增：消息配置
  const MESSAGES_PER_PAGE = 50; // 每页显示的消息条数配置，增加到50条避免消息丢失
  


  // 大模型和知识库选择相关状态
  const [selectedModel, setSelectedModel] = useState(defaultModel);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(defaultKnowledgeBase);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [models, setModels] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [modelApiMap, setModelApiMap] = useState({});
  const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // 加载指定会话的消息
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId || conversationId.startsWith('temp_') || !userInfo) {
      setMessages([]);
      setSwitchingConversation(false);
      return;
    }

    setLoadingMessages(true);
    console.log('🔄 开始加载消息，设置 switchingConversation = true');
    setSwitchingConversation(true);
    try {
      const userId = getUserId(userInfo);
      let rawMessages = [];

      if (isPlatformAgentId(currentAgent?.id)) {
        const response = await DifyAppService.listMessages(currentAgent.id, {
          conversation_id: conversationId,
          user_id: userId,
          limit: 50,
        });
        rawMessages = response?.data || [];
      } else {
        const difyAPI = createDifyChatAPI(
          process.env.REACT_APP_DIFY_WEB_BASE_URL || 'https://kb.blooart.cn',
          apiKey,
        );
        const response = await difyAPI.getMessages(userId, conversationId, '', 50);
        rawMessages = response.data || response;
      }
      
      console.log('Dify API返回的原始消息:', rawMessages);
      
      if (rawMessages && rawMessages.length > 0) {
        const transformedMessages = DifyDataTransformer.transformMessages(rawMessages);
        console.log('转换后的Dify消息:', transformedMessages);
        setMessages(transformedMessages);
      } else {
        console.log('Dify API返回空消息列表');
        setMessages([]);
      }
    } catch (error) {
      console.error('加载Dify历史消息失败:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
      console.log('✅ 消息加载完成，设置 switchingConversation = false');
      setSwitchingConversation(false);
      
      // 滚动逻辑已移至 useEffect 中处理，确保 DOM 完全渲染
    }
  }, [apiKey, userInfo, currentAgent?.id]);

  // 初始化知识库选项 - 固定两个选项
  const loadKnowledgeBases = useCallback(async () => {
    if (!showKnowledgeBase) return;
    
    setIsLoadingKnowledgeBases(true);
    try {
      // 固定两个选项：不使用知识库 和 使用知识库
      const knowledgeBaseOptions = [
        { id: 'none', name: t('common.dontUseKnowledgeBase') },
        { id: 'use', name: t('common.useKnowledgeBase') }
      ];
      
      setKnowledgeBases(knowledgeBaseOptions);
      
      // 如果没有选择知识库，默认选择"不使用知识库"
      if (!selectedKnowledgeBase) {
        setSelectedKnowledgeBase('none');
      }
      
    } catch (error) {
      console.error('初始化知识库选项失败:', error);
      // 如果初始化失败，使用默认选项
      setKnowledgeBases([
        { id: 'none', name: t('common.dontUseKnowledgeBase') },
        { id: 'use', name: t('common.useKnowledgeBase') }
      ]);
      setSelectedKnowledgeBase('none');
    } finally {
      setIsLoadingKnowledgeBases(false);
    }
  }, [showKnowledgeBase, t]);

  // 加载模型列表 - 从后端接口获取，与ChatPanelWenDaChatflowApi保持一致
  const loadModels = useCallback(async () => {
    if (!showModelSelection) return;
    
    setIsLoadingModels(true);
    try {
      const res = await axios.get('/api/available-models/');
      window.__apis = res.data;
      // 只显示已配置的模型，过滤掉无效配置
      const validApis = res.data.filter(api => 
        api.model && (api.base_url || api.model_name)
      );
      if (validApis.length === 0) {
        setModelOptions([]);
        setModels([]);
        setSelectedModel('');
        setModelApiMap({});
        message.warning(t('common.noAvailableModels'));
        return;
      }
      // 修改模型选项格式：品牌-模型名
      const opts = validApis.map(api => {
        const modelMap = {
          'openai': 'OpenAI',
          'deepseek': 'DeepSeek', 
          'gemini': 'Gemini',
          'qwen': '通义千问',
          'moonshot': 'Moonshot'
        };
        const modelDisplay = modelMap[api.model] || api.model;
        const modelName = api.model_name || api.model;
        // 只显示模型名称，不拼接厂商名称
        const displayName = modelName;
        const uniqueValue = `${api.model}_${api.model_name || api.model}`;
        return { 
          value: uniqueValue, 
          label: displayName,
          vendor: modelDisplay,
          modelName: modelName,
          originalModel: api.model, // 保存原始的model值用于API调用
          apiData: api // 保存完整的API数据
        };
      });
      // 去掉自动选择选项，直接使用模型列表
      setModelOptions(opts);
      setModels(opts);
      // 如果有模型选项，选择第一个；否则设为空
      setSelectedModel(opts.length > 0 ? opts[0].value : '');
      const map = {};
      validApis.forEach(api => { map[api.model] = api; });
      setModelApiMap(map);

    } catch (err) {
      setModelOptions([]);
      setModels([]);
      setSelectedModel('');
      setModelApiMap({});
      message.error(t('common.failedToGetModels', { error: err.response?.data?.detail || err.message }));
    } finally {
      setIsLoadingModels(false);
    }
  }, [showModelSelection, t]);

  // 加载当前智能体的 Dify App 配置（动态表单）
  const loadAppConfig = useCallback(async (appId) => {
    if (!appId || !showDynamicForm) return;
    setAppLoading(true);
    try {
      const resp = await DifyAppService.getAppConfig(appId);
      if (resp?.success && resp?.data) {
        const schema = resp.data.form_schema || [];
        const resolvedInputs = resp.data.resolved_inputs || {};
        setAppConfig(resp.data);
        setFormSchema(schema);

        const initialVals = { ...resolvedInputs };
        schema.forEach(f => {
          if (!f.hidden && f.default !== undefined && f.default !== null && f.default !== '') {
            initialVals[f.key] = f.default;
          }
        });
        if (Object.keys(initialVals).length > 0) {
          dynamicForm.setFieldsValue(initialVals);
          setDynamicInputs(initialVals);
        } else {
          dynamicForm.resetFields();
          setDynamicInputs(resolvedInputs);
        }
        console.log(
          '✅ 加载 App 配置成功:',
          resp.data.name,
          'schema:',
          schema.filter(f => !f.hidden).length,
          '可见字段, 自动注入:',
          Object.keys(resolvedInputs),
        );
      } else {
        console.warn('⚠️ 加载 App 配置返回异常:', resp);
      }
    } catch (err) {
      console.warn('⚠️ 加载 App 配置失败（降级）:', err.message);
      setFormSchema([]);
      // 平台智能体仍走后端动态路由，参数由服务端 input_bindings 注入
      if (isPlatformAgentId(appId)) {
        setAppConfig({ use_dynamic_adapter: true, mode: 'chat', resolved_inputs: {}, form_schema: [] });
        setDynamicInputs({});
      } else {
        setAppConfig(null);
      }
    } finally {
      setAppLoading(false);
    }
  }, [showDynamicForm, dynamicForm]);

  useEffect(() => {
    if (!identityStorageKey) return;
    const saved = localStorage.getItem(identityStorageKey);
    if (saved !== null) {
      setIncludeIdentity(saved === 'true' && canPassIdentity);
      return;
    }
    const studentNo = (userInfo?.student_no || '').trim();
    setIncludeIdentity(Boolean(studentNo && hasIdentityBindings));
  }, [identityStorageKey, canPassIdentity, userInfo?.student_no, hasIdentityBindings]);

  const handleIncludeIdentityChange = useCallback((checked) => {
    if (checked && !canPassIdentity) return;
    setIncludeIdentity(checked);
    if (identityStorageKey) {
      localStorage.setItem(identityStorageKey, String(checked));
    }
  }, [identityStorageKey, canPassIdentity]);

  // 监听语言变化，重新构建modelOptions（去掉auto选项）
  useEffect(() => {
    if (models.length > 0) {
      // 直接使用模型列表，不添加auto选项
      setModelOptions(models);
    }
  }, [t, models]);

  // 确保默认选择第一个模型
  useEffect(() => {
    if (models.length > 0 && (!selectedModel || selectedModel === '')) {
      // 如果没有选择模型或选择为空，则选择第一个可用模型
      setSelectedModel(models[0].value);
      console.log('🔧 自动选择第一个模型:', models[0].label);
    }
  }, [models, selectedModel]);

  // 初始化加载知识库和模型 - 使用useRef避免无限循环
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current) {
      loadKnowledgeBases();
      loadModels();
      if (currentAgent?.id && showDynamicForm) {
        loadAppConfig(currentAgent.id);
      }
      hasInitialized.current = true;
    }
  }, [loadKnowledgeBases, loadModels]); // 添加依赖项，确保函数更新时重新执行

  // 初始化会话 - 处理侧边栏状态和智能体切换
  useEffect(() => {
    // 确保userInfo存在且有效
    if (!userInfo) {
      // userInfo未加载，跳过初始化
      return;
    }

    const userId = getUserId(userInfo);

    // 如果已经初始化过，且namespace、agentId、userId都没有变化，则不重新初始化
    const currentInitKey = `${namespace}_${agentId}_${userId}`;
    if (initializedRef.current && initializedRef.current === currentInitKey) {
      return;
    }

    console.log('🔧 初始化会话，智能体ID:', agentId, '用户ID:', userId);

    // 处理侧边栏状态
    const currentLocalSidebar = getDifyChatStorageKey(LOCAL_SIDEBAR, namespace, agentId, userInfo);
    const savedSidebar = localStorage.getItem(currentLocalSidebar);
    setSidebarCollapsed(savedSidebar ? JSON.parse(savedSidebar) : false);
    
    // 清空输入框
    setInput('');

    // 标记为已初始化，记录当前的namespace、agentId、userId组合
    initializedRef.current = currentInitKey;
    
    console.log('✅ 会话初始化完成，初始化键:', currentInitKey);

  }, [namespace, agentId, userInfo?.id]); // 只依赖必要的值，移除welcomeText依赖

  // 处理智能体切换 - 完全重置组件状态
  useEffect(() => {
    if (currentAgent) {
      console.log('🔄 智能体切换检测到:', currentAgent.name, 'ID:', currentAgent.id);
      
      // 完全重置组件状态
      setMessages([]);
      setSelectedDifyConversationId(null);
      setInput('');
      setUploadedImages([]);
      setAiMessageContent('');
      setIsAiOutputting(false);
      isAiOutputtingRef.current = false;
      setSending(false);
      setAbortController(null);
      
      // 重置初始化状态，强制重新初始化
      initializedRef.current = null;
      
      // 重置模型和知识库选择到默认值
      setSelectedModel(defaultModel);
      // 重置动态表单
      setFormSchema([]);
      setAppConfig(null);
      setDynamicInputs({});
      dynamicForm?.resetFields();
      setIncludeIdentity(false);
      setSelectedKnowledgeBase(defaultKnowledgeBase);
      
      // 重新加载知识库和模型列表
      loadKnowledgeBases();
      loadModels();
      
      // 加载当前智能体的 Dify App 配置
      if (currentAgent?.id && showDynamicForm) {
        loadAppConfig(currentAgent.id);
      }
      
      // 智能体切换后，瞬间滚动到底部
      scrollToBottomInstant();
      
      console.log('✅ 智能体切换状态重置完成');
    }
  }, [currentAgent?.id, defaultModel, defaultKnowledgeBase, loadKnowledgeBases, loadModels, loadAppConfig, showDynamicForm]); // 依赖智能体ID和默认值

  // 处理welcomeText更新 - 简化版本，不再更新本地消息
  useEffect(() => {
    if (welcomeText) {
      // 不再更新本地消息，因为现在使用单一消息列表
    }
  }, []); // 移除所有依赖，避免无限循环

  // 切换历史对话 - 直接加载消息
  const handleSelectConv = useCallback((conversationId) => {
    console.log('切换会话到:', conversationId);
    setSelectedDifyConversationId(conversationId);
    // 直接加载该会话的消息
    loadMessages(conversationId);
  }, [loadMessages]);

  // 新建聊天 - 清空消息列表
  const handleNewChat = useCallback(() => {
    // 清空选中的会话ID，创建新的临时会话
    setSelectedDifyConversationId(null);
    setInput('');
    // 清空消息列表
    setMessages([]);
    message.info(t('common.newConversationCreated'));
    
    // 新建聊天后，瞬间滚动到底部
    scrollToBottomInstant();
  }, [t]);

  // 图片上传处理
  const handleImageUpload = useCallback(async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
              message.error(t('common.onlyImageFilesAllowed'));
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
              message.error(t('common.imageSizeLimit10MB'));
      return false;
    }
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await axios.post('/api/upload-chat-image/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        const backendUrl = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.host}`;
        const fullImageUrl = response.data.url.startsWith('http')
          ? response.data.url
          : `${backendUrl}${response.data.url}`;
        const imageData = {
          uid: file.uid || `file_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          name: file.name,
          url: fullImageUrl,
          serverUrl: fullImageUrl,
          filename: response.data.filename,
          status: 'done'
        };
        setUploadedImages(prev => [...prev, imageData]);
        message.success(t('common.imageUploadSuccess'));
      } else {
        message.error(t('common.imageUploadFailed'));
      }
    } catch (error) {
      console.error('图片上传失败:', error);
              message.error(t('common.imageUploadFailedWithError', { error: error.response?.data?.error || t('common.unknownError') }));
    }
    return false;
  }, [t]);

  // 拖拽图片处理
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // 只有当鼠标真正离开容器时才设置拖拽状态为false
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      message.warning(t('common.onlyImageFilesAllowed'));
      return;
    }
    
    // 处理每个图片文件
    for (const file of imageFiles) {
      await handleImageUpload(file);
    }
  }, [handleImageUpload, t]);

  // 删除图片
  const handleRemoveImage = useCallback((uid) => {
    setUploadedImages(prev => prev.filter(img => img.uid !== uid));
  }, []);

  // 预览图片
  const handlePreviewImage = useCallback((image) => {
    setPreviewImage(image.url);
    setImagePreviewVisible(true);
  }, []);

  // 当前会话对象 - 简化版本，只使用selectedDifyConversationId
  const currentConv = {
    id: selectedDifyConversationId || '',
    conversation_id: selectedDifyConversationId,
    messages: messages // 使用统一的消息列表
  };



  // 发送消息
  const handleSend = useCallback(async () => {
    if (!input.trim() && uploadedImages.length === 0) {
      message.warning(t('common.pleaseEnterContentOrUploadImage'));
      return;
    }
    setSending(true);
    const controller = new AbortController();
    setAbortController(controller);
    
    let messageContent = input.trim();
    // 如果只有图片没有文本，提供默认查询内容
    if (!messageContent && uploadedImages.length > 0) {
      messageContent = t('common.imageAnalysisRequest');
    }

    // 检查是否是模型或身份相关的问题
    const modelRelatedKeywords = ['模型', 'model', '是什么模型', '你是什么', '你是谁', '你的身份', '你的能力', '你能做什么', '你叫什么', '你是什么模型', '你是什么AI', '你是什么助手'];
    const isModelRelatedQuestion = modelRelatedKeywords.some(keyword => 
      messageContent.toLowerCase().includes(keyword.toLowerCase())
    );

    if (isModelRelatedQuestion) {
      // 直接返回指定的回答，不调用API
      const aiResponse = `我是由default模型支持的智能助手，专为Cursor IDE设计，可以帮您解决各类编程难题，请告诉我你需要什么帮助？`;
      
      // 添加AI回复消息到实时消息列表
      const aiMessage = {
        id: generateTempMessageId(),
        role: 'assistant',
        content: aiResponse,
        created_at: new Date().toISOString(),
        conversation_id: finalConversationId,
        isRealtime: false
      };
      
      setMessages(prev => {
        const newMessages = [...prev, aiMessage];
        return newMessages;
      });
      
      // 清空输入和图片，避免重复调用
      flushSync(() => {
        setInput('');
        setUploadedImages([]);
      });
      
      // 滚动到底部
      setTimeout(() => {
        scrollToBottom({ force: true });
        setUserHasScrolled(false);
        setShowBackToBottom(false);
      }, 100);
      
      setSending(false);
      return;
    }
    
    // 获取用户ID
    if (!userInfo) {
      throw new Error('用户信息未加载，请重新登录');
    }
    const userId = getUserId(userInfo);
    
    // 确定当前会话ID
    const conversationId = selectedDifyConversationId || '';
    
    // 确定最终使用的会话ID
    // 如果是临时会话ID（以temp_开头），则不传递给后端，让后端创建新的会话
    const finalConversationId = conversationId && !conversationId.startsWith('temp_') ? conversationId : null;
    
    // 如果当前没有选中的会话或会话ID是临时的，创建一个临时ID用于前端显示
    const displayConversationId = conversationId || `temp_${uuid()}`;
    if (!conversationId) {
      setSelectedDifyConversationId(displayConversationId);
    }
    
    // 将变量声明移到try块外部，确保在整个函数作用域内可访问
    let aiContent = '';
    let newConversationId = conversationId;
    let streamFinalizeScheduled = false;
    const userInputContent = messageContent; // 保存用户输入内容
    
    // files字段
    let files = [];
    if (uploadedImages.length > 0) {
      files = uploadedImages.map(img => ({
        type: 'image',
        transfer_method: 'remote_url',
        url: img.serverUrl
      }));
    }
    
    // 添加用户消息到消息列表
    const userMessage = {
      id: generateTempMessageId(), // 使用临时消息ID，确保唯一性
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      conversation_id: displayConversationId, // 使用显示用的会话ID
      files: files.length > 0 ? files : undefined // 统一使用files字段，支持图片和未来文件扩展
    };
    

    const shouldStream = responseMode === 'streaming';
    flushSync(() => {
      setMessages((prev) => {
        const next = [...prev, userMessage];
        if (shouldStream) {
          return upsertAssistantStreamMessage(next, {
            content: '',
            displayConversationId,
            thinking: true,
          });
        }
        return next;
      });
      setInput('');
      setUploadedImages([]);
      if (!conversationId) {
        setSelectedDifyConversationId(displayConversationId);
      }
    });

    // 用户发送消息后立即滚动到底部
    setTimeout(() => {
      scrollToBottom({ force: true });
      setUserHasScrolled(false); // 重置用户滚动状态
      setShowBackToBottom(false); // 确保不显示回到底部按钮
      setIsAiOutputting(true); // 开始AI输出状态
    }, 100);

    // 重置AI消息内容
    setAiMessageContent('');
    
    try {

    // ── 平台智能体走后端 DifyAdapter（自动注入 input_bindings，支持 streaming） ──
    const useDynamicAdapter = showDynamicForm && isPlatformAgentId(currentAgent?.id);
    let res;

    if (useDynamicAdapter) {
      const visibleFields = formSchema.filter(f => !f.hidden);
      if (visibleFields.length > 0) {
        try {
          await dynamicForm.validateFields();
        } catch {
          message.warning(t('common.pleaseCompleteForm') || '请先填写必填参数');
          setSending(false);
          setAbortController(null);
          return;
        }
      }

      const mergedInputs = {
        ...(includeIdentity && canPassIdentity ? (appConfig?.resolved_inputs || {}) : {}),
        ...dynamicForm.getFieldsValue(true),
        ...dynamicInputs,
      };
      setDynamicInputs(mergedInputs);

      const token = localStorage.getItem('token');
      console.log('📤 [动态路由] agentId=' + currentAgent.id + ', mode=' + (appConfig?.mode || 'chat') + ', responseMode=' + responseMode);
      res = await fetch(`/api/dify/chat/${currentAgent.id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query: messageContent,
          inputs: mergedInputs,
          files,
          conversation_id: finalConversationId || undefined,
          user_id: userId,
          response_mode: responseMode,
          include_identity: includeIdentity && canPassIdentity,
        }),
        signal: controller.signal,
      });
    } else {
      if (!apiUrl) throw new Error('未配置API地址');

      // 检查API key是否有效
      if (!apiKey || apiKey.trim() === '') {
        throw new Error('API Key 未配置或为空');
      }
      
      // 确保使用当前智能体的正确配置
      console.log('📤 发送消息，使用智能体配置:', {
        agentId: currentAgent?.id,
        apiUrl,
        apiKeyLength: apiKey ? apiKey.length : 0,
        selectedModel,
        selectedKnowledgeBase
      });
      
      // 🔍 添加消息发送前的状态跟踪
      console.log('🔍 [消息发送前] 当前状态:', {
        conversationId,
        messageContent: messageContent.substring(0, 100) + (messageContent.length > 100 ? '...' : ''),
        messagesCount: messages.length,
        isAiOutputting,
        aiMessageContent: aiMessageContent.substring(0, 50) + (aiMessageContent.length > 50 ? '...' : '')
      });
      
      // 构建请求体
      const requestBody = {
        query: messageContent,
        inputs: {
          // 大模型参数：开启大模型时传递model_name参数
          ...(showModelSelection ? {
            model_name: selectedModel
          } : {}),
          // 知识库参数：开启知识库时传递kb_name参数
          ...(showKnowledgeBase ? {
            kb_name: selectedKnowledgeBase === 'none' ? 'close' : 'auto'
          } : {})
        },
        response_mode: responseMode, // 根据配置使用流式或非流式模式
        user: userId,
        ...(finalConversationId ? { conversation_id: finalConversationId } : {}),
        ...(files.length > 0 ? { files } : {})
      };
      
      console.log('🔍 [请求体]', requestBody);
      
      res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'Authorization': `Bearer ${apiKey}` } : {}),
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
    }

      if (!res.body) throw new Error('无响应流');
      if (!res.ok) {
        // 读取错误信息
        let errMsg = '接口请求失败';
        let errorDetails = '';
        try {
          const errJson = await res.json();
          errMsg = errJson.error?.message || errJson.error || errJson.message || errJson.detail || '未知错误';
          errorDetails = JSON.stringify(errJson);
        } catch {
          errMsg = await res.text();
        }
        console.error('API请求失败:', {
          status: res.status,
          statusText: res.statusText,
          error: errMsg,
          details: errorDetails,
          apiUrl,
          hasApiKey: !!apiKey,
          apiKeyLength: apiKey ? apiKey.length : 0
        });
        throw new Error(`API请求失败 (${res.status}): ${errMsg}`);
      }
      
      // 根据响应模式处理不同的响应类型
      if (responseMode === 'blocking') {
        // 🔍 添加非流式响应处理开始跟踪
        console.log('🔍 [非流式响应开始] 开始处理JSON响应');
        
        try {
          // 解析JSON响应
          const data = await res.json();

        if (useDynamicAdapter) {
          if (!data.success) {
            throw new Error(data.error || '动态聊天接口返回失败');
          }
          aiContent = data.answer || '';
          newConversationId = data.conversation_id || newConversationId;

          const aiMessage = {
            id: generateTempMessageId(),
            role: 'assistant',
            content: aiContent,
            created_at: new Date().toISOString(),
            conversation_id: displayConversationId,
            isRealtime: false,
            metadata: data.metadata,
          };
          setMessages(prev => [...prev, aiMessage]);

          if (newConversationId && newConversationId !== conversationId) {
            setSelectedDifyConversationId(newConversationId);
            if (chatHistoryListRef.current?.addNewConversation) {
              chatHistoryListRef.current.addNewConversation({
                id: newConversationId,
                title: userInputContent.trim().slice(0, 20) + (userInputContent.length > 20 ? '...' : ''),
                messages: [
                  { role: 'user', content: userInputContent, timestamp: Date.now() },
                  { role: 'assistant', content: aiContent, timestamp: Date.now() },
                ],
                updatedAt: Date.now(),
              });
            }
          }
        } else {
        
        console.log('🔍 [非流式响应数据]', {
          event: data.event,
          hasAnswer: !!data.answer,
          answerLength: data.answer ? data.answer.length : 0,
          conversationId: data.conversation_id,
          messageId: data.message_id,
          taskId: data.task_id,
          mode: data.mode,
          metadata: data.metadata,
          usage: data.usage,
          retrieverResources: data.retriever_resources,
          createdAt: data.created_at,
          dataKeys: Object.keys(data)
        });
        
        // 验证响应格式
        if (data.event !== 'message') {
          throw new Error(`无效的响应事件类型: ${data.event}`);
        }
        
        if (!data.answer) {
          throw new Error('响应中没有找到答案内容');
        }
        
        // 提取AI回复内容
        aiContent = data.answer;
        newConversationId = data.conversation_id;
        
        console.log('🔍 [AI回复内容]', {
          answerLength: aiContent.length,
          answerPreview: aiContent.substring(0, 200) + (aiContent.length > 200 ? '...' : ''),
          conversationId: newConversationId
        });
        
        // 创建AI消息
        const aiMessage = {
          id: generateTempMessageId(),
          role: 'assistant',
          content: aiContent,
          created_at: new Date().toISOString(),
          conversation_id: displayConversationId,
          isRealtime: false,
          // 添加额外的元数据
          metadata: data.metadata,
          usage: data.usage,
          retrieverResources: data.retriever_resources,
          taskId: data.task_id,
          messageId: data.message_id
        };
        
        // 添加AI消息到消息列表
        setMessages(prev => {
          const newMessages = [...prev, aiMessage];
          console.log('🔍 [添加AI消息]', {
            messageId: aiMessage.id,
            contentLength: aiContent.length,
            totalMessages: newMessages.length
          });
          return newMessages;
        });
        
        // 异步记录token使用情况
        if (data.usage?.total_tokens || data.usage?.prompt_tokens || data.usage?.completion_tokens) {
          const tokenData = {
            apikey: apiKey || '',
            model: selectedModel || 'unknown',
            prompt_tokens: data.usage.prompt_tokens || 0,
            completion_tokens: data.usage.completion_tokens || 0,
            total_tokens: data.usage.total_tokens || 0,
            timestamp: Date.now()
          };
          // 异步记录，不阻塞UI
          setTimeout(() => {
            try {
              console.log('Token使用情况:', tokenData);
            } catch (err) {
              console.warn('记录token使用情况失败:', err);
            }
          }, 0);
        }
        
                        // 🔍 添加非流式响应处理完成跟踪
         console.log('🔍 [非流式响应处理完成]', {
           aiContent,
           aiContentLength: aiContent.length,
           newConversationId,
           conversationId,
           messagesCount: messages.length + 1
         });
         
         // 更新会话的conversation_id
         if (newConversationId && newConversationId !== conversationId) {
           // 🔍 添加会话ID更新跟踪
           console.log('🔍 [会话ID更新]', {
             oldConversationId: conversationId,
             newConversationId,
             aiContentLength: aiContent.length
           });
           
           // 更新selectedDifyConversationId
           setSelectedDifyConversationId(newConversationId);
           
           // 如果是新建会话（从temp_开头变为正式ID），添加到历史列表
           if (chatHistoryListRef.current && chatHistoryListRef.current.addNewConversation) {
             // 创建新会话对象并添加到历史列表
             const newConversation = {
               id: newConversationId,
               title: userInputContent.trim().slice(0, 20) + (userInputContent.length > 20 ? '...' : ''),
               messages: [
                 {
                   role: 'user',
                   content: userInputContent,
                   timestamp: Date.now()
                 },
                 {
                   role: 'assistant',
                   content: aiContent,
                   timestamp: Date.now()
                 }
               ],
               updatedAt: Date.now()
             };
             // 创建新会话对象并添加到历史列表
             chatHistoryListRef.current.addNewConversation(newConversation);
           }
         }
        }
         
       } catch (parseError) {
         console.error('🔍 [JSON解析失败]', {
           error: parseError.message,
           errorType: parseError.constructor.name,
           responseText: await res.text()
         });
         throw new Error(`响应解析失败: ${parseError.message}`);
       }
       
     } else if (responseMode === 'streaming') {
       // 🔍 添加流式响应处理开始跟踪
       console.log('🔍 [流式响应开始] 开始处理SSE流数据');
       
       if (!res.body) throw new Error('无响应流');
       
       const reader = res.body.getReader();
       let done = false;
       let buffer = '';
       let streamEnded = false;
       
       // 添加取消信号监听器
       controller.signal.addEventListener('abort', () => {
         streamEnded = true;
         done = true;
       });
       
       const decoder = new TextDecoder();
       const { extractStreamAnswer } = createStreamAnswerExtractor();
       const findAssistantStreamIndex = (list) =>
         list.findIndex((msg) => msg.role === 'assistant' && (msg.isThinking || msg.isRealtime));

       const streamFlusher = createStreamFlusher(
         setMessages,
         (prev, payload) => upsertAssistantStreamMessage(prev, {
           ...payload,
           displayConversationId,
         }),
         (content) => {
           setAiMessageContent(content);
         },
       );

       const applyAssistantStreamContent = (content, opts = {}) => {
         streamFlusher.push({ content, ...opts });
       };

       const finalizeAssistantStream = (content) => {
         streamFinalizeScheduled = true;
         streamFlusher.flushNow({
           content,
           thinking: false,
           isRealtime: true,
         });

         const typingDelay = estimateStreamRevealMs(content.length);
         window.setTimeout(() => {
           streamFlusher.flushNow({
             content,
             thinking: false,
             isRealtime: false,
           });
           setIsAiOutputting(false);
           setAiMessageContent('');
         }, typingDelay);
       };

       try {
         let loopCount = 0;
         while (!done && !streamEnded) {
           loopCount++;

           if (controller.signal.aborted) {
             break;
           }

           const { value, done: readDone } = await reader.read();
           done = readDone;

           if (controller.signal.aborted) {
             break;
           }

           if (value) {
             buffer += decoder.decode(value, { stream: !done });
           }

           buffer = buffer.replace(/\r\n/g, '\n');
           const parts = buffer.split('\n\n');
           buffer = parts.pop() || '';

           for (const part of parts) {
             if (controller.signal.aborted) {
               break;
             }

             const dataLine = part
               .split('\n')
               .map((line) => line.trim())
               .find((line) => line.startsWith('data:'));
             if (!dataLine) continue;

             const jsonStr = dataLine.replace(/^data:\s?/, '');
             if (!jsonStr.trim() || jsonStr === '[DONE]') continue;

             let cleanedJsonStr = jsonStr;
             try {
               cleanedJsonStr = cleanedJsonStr.replace(/\\u([0-9a-fA-F]{1,4})/g, (match, hex) => {
                 const padded = hex.length < 4 ? hex.padEnd(4, '0') : hex.substring(0, 4);
                 return `\\u${padded}`;
               });

               const data = JSON.parse(cleanedJsonStr);
               const chunk = extractStreamAnswer(data);

               if (chunk) {
                 aiContent = mergeStreamChunk(aiContent, chunk, data);
                 applyAssistantStreamContent(aiContent, {
                   thinking: false,
                   isRealtime: true,
                 });
               }

               if (data.conversation_id) {
                 newConversationId = data.conversation_id;
               }

               if (data.event === 'message_end' || data.event === 'workflow_finished') {
                 if (data.answer && data.answer.trim()) {
                   aiContent = mergeStreamChunk(aiContent, data.answer, data);
                 }

                 if (data.event === 'message_end') {
                   streamEnded = true;
                   finalizeAssistantStream(aiContent);

                   if (data.usage?.total_tokens || data.usage?.prompt_tokens || data.usage?.completion_tokens) {
                     const tokenData = {
                       apikey: apiKey || '',
                       model: selectedModel || 'unknown',
                       prompt_tokens: data.usage.prompt_tokens || 0,
                       completion_tokens: data.usage.completion_tokens || 0,
                       total_tokens: data.usage.total_tokens || 0,
                       timestamp: Date.now(),
                     };
                     setTimeout(() => {
                       try {
                         console.log('Token使用情况:', tokenData);
                       } catch (err) {
                         console.warn('记录token使用情况失败:', err);
                       }
                     }, 0);
                   }

                   break;
                 }
               }
               
               // 处理错误事件
               if (data.event === 'error' && data.error) {
                 console.error('🔍 [错误事件]', {
                   loopCount,
                   error: data.error,
                   event: data.event,
                   errorType: typeof data.error,
                   errorMessage: data.error.message || data.error,
                   currentAiContentLength: aiContent.length,
                   hasValidContent: aiContent.trim().length > 0
                 });
                 
                 // 检查是否已经有有效的内容
                 if (aiContent.trim().length > 0) {
                   // 将现有内容标记为最终内容
                   setMessages(prev => {
                     const existingAiMessageIndex = prev.findIndex(
                       (msg) => msg.role === 'assistant' && (msg.isThinking || msg.isRealtime),
                     );
                     if (existingAiMessageIndex !== -1) {
                       return prev.map((msg, index) => 
                         index === existingAiMessageIndex 
                           ? { ...msg, content: aiContent, isThinking: false, isRealtime: false }
                           : msg
                       );
                     }
                     return prev;
                   });
                   
                   // 添加错误提示，但不覆盖主要内容
                   const errorNote = {
                     id: generateTempMessageId(),
                     role: 'assistant',
                     content: `\n\n---\n*${t('common.configErrorNote')}*`,
                     created_at: new Date().toISOString(),
                     conversation_id: displayConversationId,
                     isError: true,
                     isNote: true
                   };
                   
                   setMessages(prev => [...prev, errorNote]);
                 } else {
                   // 如果没有有效内容，显示错误消息
                   const errorMessage = {
                     id: generateTempMessageId(),
                     role: 'assistant',
                     content: `${t('common.requestError')}: ${data.error.message || data.error}`,
                     created_at: new Date().toISOString(),
                     conversation_id: displayConversationId,
                     isError: true
                   };
                   
                   setMessages((prev) => [
                     ...prev.filter((msg) => !msg.isThinking),
                     errorMessage,
                   ]);
                 }
                 
                 // 重置AI输出状态
                 setIsAiOutputting(false);
                 setAiMessageContent('');
                 
                 // 标记流已结束
                 streamEnded = true;
                 break;
               }
               
             } catch (err) {
               // SSE流解析异常，记录错误但不中断流
               console.error('🔍 [SSE流解析失败]', {
                 loopCount,
                 error: err.message,
                 errorType: err.constructor.name,
                 originalData: jsonStr.substring(0, 200) + (jsonStr.length > 200 ? '...' : ''),
                 cleanedData: cleanedJsonStr.substring(0, 200) + (cleanedJsonStr.length > 200 ? '...' : '')
               });
               
               continue;
             }
           }
           
           // 如果流已经结束且没有剩余数据，退出循环
           if (done && !buffer.trim()) {
             break;
           }
           
           // 如果streamEnded为true，立即退出外层循环
           if (streamEnded) {
             break;
           }
         }
         
       } finally {
         // 确保reader被正确释放，防止请求保持打开状态
         try {
           reader.releaseLock();
         } catch (err) {
           console.warn('释放reader锁时出错:', err);
         }
       }

       if (!streamEnded) {
         if (aiContent.trim()) {
           finalizeAssistantStream(aiContent);
         } else {
           setMessages((prev) => prev.filter((msg) => !msg.isThinking));
           setIsAiOutputting(false);
           setAiMessageContent('');
         }
       }
       
       // 更新会话的conversation_id（流式模式）
       if (newConversationId && newConversationId !== conversationId) {
         setSelectedDifyConversationId(newConversationId);
         
         if (chatHistoryListRef.current && chatHistoryListRef.current.addNewConversation) {
           const newConversation = {
             id: newConversationId,
             title: userInputContent.trim().slice(0, 20) + (userInputContent.length > 20 ? '...' : ''),
             messages: [
               {
                 role: 'user',
                 content: userInputContent,
                 timestamp: Date.now()
               },
               {
                 role: 'assistant',
                 content: aiContent,
                 timestamp: Date.now()
               }
             ],
             updatedAt: Date.now()
           };
           chatHistoryListRef.current.addNewConversation(newConversation);
         }
       }
     }
     
     if (!streamFinalizeScheduled) {
       setIsAiOutputting(false);
       setAiMessageContent('');
     }
     
     // 清空输入和图片，使用flushSync避免抖动
     flushSync(() => {
       setInput('');
       setUploadedImages([]);
     });
     
     // 滚动到底部
     setTimeout(() => {
       scrollToBottom({ force: true });
       setUserHasScrolled(false);
       setShowBackToBottom(false);
     }, 100);
    } catch (e) {
      // 失败时在AI回复气泡显示错误
      console.error('🔍 [智能体聊天失败]', {
        error: e.message,
        errorType: e.constructor.name,
        stack: e.stack,
        aiContent: aiContent || '',
        aiContentLength: (aiContent || '').length,
        conversationId,
        newConversationId
      });
      let errorMessage = e.message || 'AI回复失败';

      // 根据错误类型提供更详细的错误信息
      if (errorMessage.includes('Load failed') || errorMessage.includes('fetch')) {
        errorMessage = '网络连接失败，请检查API地址是否正确或网络连接是否正常';
      } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        errorMessage = 'API密钥无效，请检查API Key配置';
      } else if (errorMessage.includes('404')) {
        errorMessage = 'API地址不存在，请检查API URL配置';
      } else if (errorMessage.includes('500')) {
        errorMessage = '服务器内部错误，请稍后重试';
      } else if (errorMessage.includes('timeout')) {
        errorMessage = '请求超时，请检查网络连接';
      }

      // 移除错误的AI消息，但保留用户消息，并添加错误消息
      setMessages(prev => {
        const filteredMessages = prev.filter(msg => msg.role !== 'assistant' || !msg.isRealtime);
        
        // 🔍 添加错误时消息清理跟踪
        console.log('🔍 [错误时消息清理]', {
          originalCount: prev.length,
          filteredCount: filteredMessages.length,
          removedCount: prev.length - filteredMessages.length
        });
        
        // 添加错误消息给用户
        const errorMessageObj = {
          id: generateTempMessageId(),
          role: 'assistant',
          content: `❌ **聊天失败**\n\n${errorMessage}\n\n请检查网络连接或稍后重试。`,
          created_at: new Date().toISOString(),
          conversation_id: displayConversationId,
          isError: true
        };
        
        return [...filteredMessages, errorMessageObj];
      });
      
      // 确保在错误时也重置所有状态
      setIsAiOutputting(false);
      setAiMessageContent('');
      
      // 🔍 添加错误时状态重置跟踪
      console.log('🔍 [错误时状态重置]', {
        isAiOutputting: false,
        aiMessageContent: '',
        errorMessage
      });
      
      // 注意：reader变量在try块内部定义，在catch块中可能不在作用域内
      // 资源清理已经在finally块中处理，这里不需要重复处理
    }
    
          // 🔍 添加函数结束状态跟踪
      console.log('🔍 [函数结束]', {
        isSending: false,
        abortController: null,
        finalAiContent: aiContent || '',
        finalAiContentLength: (aiContent || '').length
      });
    
    setSending(false);
    setAbortController(null);
    if (!streamFinalizeScheduled) {
      setIsAiOutputting(false);
      setAiMessageContent('');
    }
  }, [input, uploadedImages, currentAgent?.id, apiKey, apiUrl, selectedDifyConversationId, showModelSelection, selectedModel, showKnowledgeBase, selectedKnowledgeBase, userInfo?.id, responseMode, formSchema, dynamicInputs, appConfig, showDynamicForm, dynamicForm, includeIdentity, canPassIdentity, t]);

  // 取消发送
  const handleCancelSend = useCallback(() => {
    if (abortController) {
      // 立即重置状态，让用户看到响应
      setSending(false);
      setIsAiOutputting(false);
      setAiMessageContent('');
      
      // 取消请求
      abortController.abort();
      
      // 清理状态
      setAbortController(null);
      
      // 移除实时消息，但保留用户消息
      setMessages((prev) => prev.filter(
        (msg) => msg.role !== 'assistant' || (!msg.isRealtime && !msg.isThinking),
      ));
    }
  }, [abortController]);

  // 聊天历史数据源 - 简化版本
  const historyList = [];

  // 使用 useMemo 优化消息显示计算
  const messagesForDisplay = useMemo(() => {
    // 直接返回统一的消息列表
    return messages;
  }, [messages]);

  // 思考状态由左侧 assistant 气泡展示；发送中状态仍用于输入框/停止按钮
  const showMessageLoading = false;
  const isSending = sending;

  // 保存到本地存储 - 简化版本，只保存侧边栏状态
  const saveToLocal = useCallback((sidebar) => {
    // 使用统一的存储键生成函数
    const currentLocalSidebar = getDifyChatStorageKey(LOCAL_SIDEBAR, namespace, agentId, userInfo);

    localStorage.setItem(currentLocalSidebar, JSON.stringify(sidebar));
  }, [agentId, namespace, userInfo?.id]); // 只依赖必要的值

  // sidebarCollapsed 变更时写入本地
  useEffect(() => {
    if (userInfo) { // 确保userInfo存在时才保存
      // 直接在这里保存，避免依赖saveToLocal函数
      const currentLocalSidebar = getDifyChatStorageKey(LOCAL_SIDEBAR, namespace, agentId, userInfo);
      localStorage.setItem(currentLocalSidebar, JSON.stringify(sidebarCollapsed));
    }
  }, [sidebarCollapsed, namespace, agentId, userInfo?.id]); // 只依赖必要的值

  // 删除会话 - 简化版本，只处理Dify会话删除
  const handleDeleteConv = useCallback((id) => {
    // 只处理Dify会话删除，本地不再维护会话列表
    // ChatHistoryList会自己处理Dify会话的删除
  }, []);



  // 处理输入框的onChange事件 - 使用useCallback优化性能
  const onInputChange = useCallback((e) => {
    const value = e.target.value;
    // 直接更新输入值，不触发防抖，避免抖动
    setInput(value);
  }, []); // 空依赖数组，避免不必要的重新创建

  // 聊天输入区
  const renderInputAreaWithImages = () => (
    <div>
      {/* 大模型和知识库选择区域 */}
      {(showModelSelection || showKnowledgeBase) && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: 8,
          padding: '6px 8px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          {showModelSelection && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Select
                className="chat-yellow-select"
                value={selectedModel}
                onChange={setSelectedModel}
                style={{
                  borderRadius: 16,
                  border: 'none',
                  minWidth: 140,
                  height: 32,
                  fontSize: 14,
                  color: '#231a17',
                }}
                dropdownStyle={{ borderRadius: 8 }}
                loading={isLoadingModels}
                placeholder={t('common.selectModel')}
                size="small"
              >
                {modelOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>
          )}
          
          {showKnowledgeBase && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Select
                className="chat-yellow-select"
                value={selectedKnowledgeBase}
                onChange={setSelectedKnowledgeBase}
                style={{
                  borderRadius: 16,
                  border: 'none',
                  minWidth: 140,
                  height: 32,
                  fontSize: 14,
                  color: '#231a17',
                }}
                dropdownStyle={{ borderRadius: 8 }}
                loading={isLoadingKnowledgeBases}
                placeholder={t('common.selectKnowledgeBase')}
                size="small"
              >
                {knowledgeBases.map(kb => (
                  <Option key={kb.id} value={kb.id}>
                    {kb.name}
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      )}

      {/* 动态表单区域（Dify App input_form schema） */}
      {formSchema && formSchema.some(f => !f.hidden) && (
        <div style={{
          marginBottom: 8,
          padding: '12px',
          background: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
        }}>
          <Text type="secondary" style={{ marginBottom: 8, display: 'block', fontSize: 12 }}>
            {t('difyAppInputParams') || '智能体参数'}
          </Text>
          <DynamicForm
            schema={formSchema}
            form={dynamicForm}
            loading={appLoading}
            onValuesChange={(changed, all) => setDynamicInputs(all)}
          />
        </div>
      )}

      {uploadedImages.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: 8,
          padding: '8px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          {uploadedImages.map((image) => (
            <div key={image.uid} style={{ position: 'relative' }}>
              <img
                src={image.url}
                alt={image.name}
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0'
                }}
                onClick={() => handlePreviewImage(image)}
              />
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#ff4d4f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '8px'
                }}
                onClick={() => handleRemoveImage(image.uid)}
              />
            </div>
          ))}
        </div>
      )}
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'flex-end', 
          gap: '8px', 
          padding: '4px 0',
          position: 'relative',
          border: isDragOver ? '2px dashed #1890ff' : '2px solid transparent',
          borderRadius: '8px',
          backgroundColor: isDragOver ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
          transition: 'all 0.2s ease'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {canPassIdentity && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <span style={{ fontSize: 10, lineHeight: 1, color: '#666' }}>学号</span>
            <Tooltip title={includeIdentity ? '学号：已开启' : '学号：已关闭'}>
              <Switch
                checked={includeIdentity}
                onChange={handleIncludeIdentityChange}
                size="small"
              />
            </Tooltip>
          </div>
        )}
        <div style={{ position: 'relative', flex: 1 }}>
          <Input.TextArea
            value={input}
            onChange={onInputChange}
            placeholder={isDragOver ? t('common.dropImageHere') : t('common.courseQaPlaceholder')}
            variant="borderless"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{
              fontSize: 14,
              background: 'transparent',
              boxShadow: 'none',
              outline: 'none',
              resize: 'none',
              border: 'none',
              padding: '8px 12px',
              marginBottom: 4,
              minHeight: 48,
              maxHeight: 120,
              width: '100%',
              lineHeight: '1.5',
              // 添加平滑过渡，减少抖动感
              transition: 'height 0.1s ease-out',
            }}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onKeyDown={useCallback(e => {
              if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
                e.preventDefault();
                // 直接检查当前输入值，避免依赖input状态
                const currentInput = e.target.value;
                if (currentInput.trim() || uploadedImages.length > 0) handleSend();
              }
            }, [uploadedImages.length, isComposing, handleSend])}
            disabled={isSending}
          />
          {isDragOver && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(24, 144, 255, 0.9)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              zIndex: 10,
              pointerEvents: 'none'
            }}>
              {t('common.dropImageHere')}
            </div>
          )}
        </div>
        {showImageUpload && (
          <Upload
            accept="image/*"
            beforeUpload={handleImageUpload}
            showUploadList={false}
            disabled={isSending}
          >
            <Button
              type="text"
              icon={<PictureOutlined />}
              style={{
                width: 48,
                height: 48,
                fontSize: 18,
                color: '#666',
                border: '1px solid #e0e0e0',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={t('common.uploadImage')}
            />
          </Upload>
        )}
        <Button
          type="primary"
          shape="circle"
          icon={isSending ? <StopOutlined /> : <SendOutlined />}
          onClick={isSending ? handleCancelSend : handleSend}
          loading={false}
          style={{
            width: 48,
            height: 48,
            fontSize: 20,
            boxShadow: 'none',
            background: isSending ? '#ff4d4f' : '#222',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        />
      </div>
      <div className="chat-ai-disclaimer">
        {t('common.aiContentDisclaimer', '内容由 AI 生成，请仔细甄别')}
      </div>
    </div>
  );

  return (
    <div className={`chat-workspace${agentSidebar ? ' chat-workspace--unified' : ''}`} style={restProps.customStyles}>
      {!sidebarCollapsed && (
        agentSidebar ? (
          <aside className="workspace-sidebar">
            {agentSidebar}
            <ChatConversationSidebar
              embedded
              onNewChat={handleNewChat}
              newChatLabel={t('common.newConversation')}
            >
              <ChatHistoryList
                ref={chatHistoryListRef}
                conversations={[]}
                currentId={selectedDifyConversationId}
                onSelect={handleSelectConv}
                onDelete={handleDeleteConv}
                useDifyHistory={true}
                agentId={currentAgent?.id}
                difyBaseURL={process.env.REACT_APP_DIFY_WEB_BASE_URL || 'https://kb.blooart.cn'}
                difyApiKey={apiKey}
                userInfo={userInfo}
                onConversationsLoaded={(difyConversations) => {
                  if (difyConversations.length > 0 && !selectedDifyConversationId) {
                    const firstConversation = difyConversations[0];
                    setSelectedDifyConversationId(firstConversation.id);
                    loadMessages(firstConversation.id);
                  }
                }}
                onConversationDeleted={(deletedConversationId, remainingConversations) => {
                  if (deletedConversationId === selectedDifyConversationId) {
                    if (remainingConversations.length > 0) {
                      const firstConversation = remainingConversations[0];
                      setSelectedDifyConversationId(firstConversation.id);
                      loadMessages(firstConversation.id);
                    } else {
                      setSelectedDifyConversationId(null);
                      setMessages([]);
                    }
                  }
                }}
              />
            </ChatConversationSidebar>
          </aside>
        ) : (
          <ChatConversationSidebar
            onNewChat={handleNewChat}
            newChatLabel={t('common.newConversation')}
          >
            <ChatHistoryList
              ref={chatHistoryListRef}
              conversations={[]}
              currentId={selectedDifyConversationId}
              onSelect={handleSelectConv}
              onDelete={handleDeleteConv}
              useDifyHistory={true}
              agentId={currentAgent?.id}
              difyBaseURL={process.env.REACT_APP_DIFY_WEB_BASE_URL || 'https://kb.blooart.cn'}
              difyApiKey={apiKey}
              userInfo={userInfo}
              onConversationsLoaded={(difyConversations) => {
                if (difyConversations.length > 0 && !selectedDifyConversationId) {
                  const firstConversation = difyConversations[0];
                  setSelectedDifyConversationId(firstConversation.id);
                  loadMessages(firstConversation.id);
                }
              }}
              onConversationDeleted={(deletedConversationId, remainingConversations) => {
                if (deletedConversationId === selectedDifyConversationId) {
                  if (remainingConversations.length > 0) {
                    const firstConversation = remainingConversations[0];
                    setSelectedDifyConversationId(firstConversation.id);
                    loadMessages(firstConversation.id);
                  } else {
                    setSelectedDifyConversationId(null);
                    setMessages([]);
                  }
                }
              }}
            />
          </ChatConversationSidebar>
        )
      )}

      <div className="chat-main-panel">
        <div className="chat-main-panel__toolbar">
          <Button
            type="text"
            className="chat-main-panel__collapse-btn"
            icon={sidebarCollapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {!selectedDifyConversationId && messages.length === 0 ? (
          <div className="chat-main-panel__welcome">
            <div className="chat-main-panel__welcome-card">
              <div style={{ marginBottom: 16 }}>
                <AgentAvatar agent={currentAgent} size={72} selected />
              </div>
              <div className="chat-main-panel__welcome-name">
                {currentAgent?.name || '智能助手'}
              </div>
              <div className="chat-main-panel__welcome-intro">
                {currentConv.messages[0]?.content || currentAgent?.welcomeText || welcomeText}
              </div>
            </div>
            <div className="chat-main-panel__composer-wrap">
              <div className="chat-main-panel__composer">
                {renderInputAreaWithImages()}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div ref={chatContainerRef} className="chat-main-panel__messages">
              <ChatMessageList
                messages={messagesForDisplay}
                chatListRef={chatListRef}
                currentAgent={currentAgent}
                showLoading={showMessageLoading}
                loadingTip={t('common.aiThinking')}
                scrollable={false}
                customStyles={{
                  background: 'transparent',
                }}
              />
              <div ref={messagesEndRef} style={{ height: 1 }} />
              {showBackToBottom && (
                <div style={{
                  position: 'sticky',
                  bottom: 20,
                  right: 20,
                  zIndex: 1000,
                  alignSelf: 'flex-end',
                  marginTop: 'auto',
                  marginBottom: 20,
                }}
                >
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<DownOutlined />}
                    onClick={() => {
                      scrollToBottom({ force: true, behavior: 'smooth' });
                      setUserHasScrolled(false);
                      setShowBackToBottom(false);
                      setAutoScroll(true);
                    }}
                    style={{
                      width: 40,
                      height: 40,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      background: theme.PRIMARY_COLOR,
                      border: 'none',
                    }}
                  />
                </div>
              )}
            </div>
            <div className="chat-main-panel__composer-wrap">
              <div className="chat-main-panel__composer">
                {renderInputAreaWithImages()}
              </div>
            </div>
          </>
        )}
      </div>
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: imagePreviewVisible,
          src: previewImage,
          onVisibleChange: (visible) => setImagePreviewVisible(visible),
        }}
      />
    </div>
  );
};

export default React.memo(ChatPanelDifyChatflowApi);
